import { Mail } from 'lucide-react'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AuthLayout from '../components/AuthLayout'
import { AuthService } from '../utils/authService'

export default function ForgetPassword() {
  const [formData, setFormData] = useState({
    email: '',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.email) {
      setError('Please enter your email address')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const response = await AuthService.forgotPassword({
        email: formData.email,
      })

      if (response.statusCode === 200) {
        setIsSuccess(true)
        // Store email for reset password flow
        localStorage.setItem('resetPasswordEmail', formData.email)
        // Navigate to reset password page after a short delay
        setTimeout(() => {
          navigate('/reset-password')
        }, 2000)
      } else {
        setError('Failed to send reset email. Please try again.')
      }
    } catch (err: any) {
      // Extract the exact error message from API response
      let errorMessage = 'Failed to send reset email. Please try again.'

      if (err.response?.message) {
        errorMessage = err.response.message
      } else if (err.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthLayout>
      <div className='w-full max-w-[400px] mx-auto'>
        <div className='mb-8'>
          <h1 className='text-[36px] font-[700] text-[#181D27] mb-3 leading-[38px] text-start'>
            Forgot Password
          </h1>
          <p className='text-[16px] text-[#667085] text-start font-[400] leading-[24px]'>
            {isSuccess ? (
              <>
                A password reset link has been sent to your email address:{' '}
                <b className='font-[700] text-[#181D27]'>{formData.email}</b>{' '}
                Please follow the link inside to continue.
              </>
            ) : (
              'Enter your email address and we will send you a password reset link.'
            )}
          </p>
        </div>

        <form onSubmit={handleSubmit} className='space-y-5'>
          {error && (
            <div className='p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md'>
              {error}
            </div>
          )}

          {isSuccess && (
            <div className='p-3 text-sm text-green-600 bg-green-50 border border-green-200 rounded-md'>
              Password reset link sent successfully! Redirecting...
            </div>
          )}

          <div className='flex flex-col items-start justify-center w-full'>
            <label
              htmlFor='email'
              className='block text-[14px] font-medium text-[#344054] mb-[6px]'
            >
              Email
            </label>
            <div className='relative w-full'>
              <Mail className='absolute left-[14px] top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085]' />
              <input
                id='email'
                type='email'
                placeholder='olivia@untitledui.com'
                value={formData.email}
                onChange={e =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className='w-full h-[44px] pl-[44px] pr-[14px] border border-[#D0D5DD] rounded-[8px] text-[16px] placeholder:text-[#667085] text-black outline-none bg-white'
                required
                disabled={isLoading || isSuccess}
              />
            </div>
          </div>

          <button
            type='submit'
            disabled={isLoading || isSuccess}
            className='w-full h-[44px] bg-[#001B38] cursor-pointer text-white text-[16px] font-semibold rounded-[8px] transition-colors duration-200 mt-6 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {isLoading ? 'Sending...' : isSuccess ? 'Sent!' : 'Reset Password'}
          </button>
        </form>
      </div>
    </AuthLayout>
  )
}
